import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { orderService } from '../../services/orderService';
import { useLocation } from '../../contexts/LocationContext';
import api from '../../services/api';

const OrderDetails = ({ 
  order, 
  onClose, 
  onEdit, 
  onStatusChange, 
  getStatusColor, 
  getStatusLabel 
}) => {
  const navigate = useNavigate();
  const { availableLocations } = useLocation();
  const [loading, setLoading] = useState(false);
  const [generatingReceipt, setGeneratingReceipt] = useState(false);
  const [fullOrderData, setFullOrderData] = useState(order);
  const [sendingSms, setSendingSms] = useState(false);
  const [smsMessage, setSmsMessage] = useState({ text: '', type: '' });

  // Pobierz pełne szczegóły zamówienia jeśli nie ma pozycji
  useEffect(() => {
    const loadFullOrderData = async () => {
      if (!order?.pozycje || order.pozycje.length === 0) {
        try {
          setLoading(true);
          const result = await orderService.getOrder(order.id);
          if (result.success) {
            setFullOrderData(result.data);
          } else {
            console.error('Failed to load order data:', result.error);
          }
        } catch (error) {
          console.error('Error loading order data:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    if (order?.id) {
      loadFullOrderData();
    }
  }, [order?.id]);

  // Użyj pełnych danych jeśli są dostępne
  const orderData = fullOrderData || order;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    if (!price) return '0,00 zł';
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(price);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setLoading(true);
      await onStatusChange(orderData.id, newStatus);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReceipt = async () => {
    try {
      setGeneratingReceipt(true);
      
      // Konwertuj zamówienie na transakcję POS (otwartą, do edycji)
      const response = await api.post(`/orders/${orderData.id}/convert-to-pos`);
      
      const result = response.data;
      
      if (result.success) {
        // Zamknij modal i przekieruj do POS z otwartą transakcją
        onClose();
        
        // Użyj navigate zamiast window.location.href aby zachować stan sesji
        navigate(`/pos?transaction_id=${result.data.transaction_id}&from_order=${orderData.id}`);
        
      } else {
        toast.error('Błąd konwersji zamówienia: ' + result.error);
      }
    } catch (error) {
      toast.error('Wystąpił błąd podczas konwersji zamówienia');
      console.error('Error converting order to POS:', error);
    } finally {
      setGeneratingReceipt(false);
    }
  };

  // Funkcja wysyłania SMS do klienta
  const handleSendSms = async (templateType) => {
    try {
      setSendingSms(true);
      setSmsMessage({ text: '', type: '' });

      const response = await api.post(`/sms/send-order/${orderData.id}`, { template: templateType });

      const result = response.data;

      if (result.success) {
        setSmsMessage({ text: '✅ SMS wysłany pomyślnie!', type: 'success' });
      } else {
        setSmsMessage({ text: `❌ ${result.message}`, type: 'error' });
      }
    } catch (error) {
      setSmsMessage({ text: '❌ Błąd połączenia z serwerem', type: 'error' });
      console.error('Error sending SMS:', error);
    } finally {
      setSendingSms(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'var(--card-bg)',
        borderRadius: '8px',
        maxWidth: '900px',
        width: '95%',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
      }}>
        {/* Nagłówek modalu - styl POS */}
        <div style={{ 
          background: 'linear-gradient(135deg, var(--info) 0%, var(--info) 100%)',
          color: '#fff' ,
          padding: '0.5rem 0.75rem',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600' }}>
              🛒 Szczegóły zamówienia {orderData.numer_zamowienia}
            </h3>
            <div style={{ fontSize: '10px', opacity: 0.9, marginTop: '2px' }}>
              📅 {formatDate(orderData.data_zamowienia)} • 🏷️ {getStatusLabel(orderData.status)}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '4px 10px',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border)',
              cursor: 'pointer',
              fontSize: '11px'
            }}
          >
            ✕ Zamknij
          </button>
        </div>

        <div style={{ padding: '0.5rem', overflow: 'auto', maxHeight: 'calc(90vh - 120px)' }}>
          <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: 'repeat(2, 1fr)' }}>
            
            {/* Informacje podstawowe */}
            <div style={{ padding: '0.5rem', backgroundColor: 'var(--bg)', borderRadius: '4px', borderLeft: '3px solid var(--info)' }}>
              <h4 style={{ margin: '0 0 0.4rem 0', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                📋 Informacje podstawowe
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.3rem', fontSize: '10px' }}>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>Numer:</span><br/>
                  <strong style={{ color: 'var(--primary)' }}>{orderData.numer_zamowienia}</strong>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>Status:</span><br/>
                  <select
                    value={orderData.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={loading}
                    style={{
                      padding: '4px 10px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      backgroundColor: getStatusColor(orderData.status),
                      color: 'white'
                    }}
                  >
                    <option value="pending">Oczekujące</option>
                    <option value="processing">W realizacji</option>
                    <option value="completed">Zrealizowane</option>
                    <option value="cancelled">Anulowane</option>
                  </select>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>Typ realizacji:</span><br/>
                  <strong>
                    {order.typ_realizacji === 'do_odbioru' && '🏪 Do odbioru'}
                    {order.typ_realizacji === 'dostawa' && '🚚 Dostawa'}
                    {order.typ_realizacji === 'na_miejscu' && '🍽️ Na miejscu'}
                  </strong>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>Lokalizacja:</span><br/>
                  <strong>📍 {availableLocations.find(loc => loc.id === order.location_id)?.nazwa || `ID: ${order.location_id}`}</strong>
                </div>
              </div>
              {order.uwagi && (
                <div style={{ marginTop: '0.4rem', padding: '0.3rem', backgroundColor: 'var(--warning-light)', borderRadius: '3px', fontSize: '10px' }}>
                  💬 <strong>Uwagi:</strong> {order.uwagi}
                </div>
              )}
            </div>

            {/* Dane klienta */}
            <div style={{ padding: '0.5rem', backgroundColor: 'var(--bg)', borderRadius: '4px', borderLeft: '3px solid var(--success)' }}>
              <h4 style={{ margin: '0 0 0.4rem 0', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                👤 Dane klienta
              </h4>
              <div style={{ fontSize: '10px' }}>
                <div style={{ marginBottom: '0.3rem' }}>
                  <strong>
                    {order.nazwa_firmy || 
                     `${order.imie || ''} ${order.nazwisko || ''}`.trim() || 
                     'Nieznany klient'}
                  </strong>
                  <span style={{ 
                    marginLeft: '6px',
                    padding: '1px 4px',
                    backgroundColor: 'var(--border)',
                    borderRadius: '3px',
                    fontSize: '9px'
                  }}>
                    {order.typ_klienta === 'osoba_fizyczna' ? 'Osoba fizyczna' : 'Firma'}
                  </span>
                </div>
                {order.email && (
                  <div style={{ color: 'var(--text-muted)' }}>📧 {order.email}</div>
                )}
                {order.telefon && (
                  <div style={{ color: 'var(--text-muted)' }}>📞 {order.telefon}</div>
                )}
                {(order.adres || order.miasto || order.kod_pocztowy) && (
                  <div style={{ color: 'var(--text-muted)' }}>
                    📍 {[order.adres, order.kod_pocztowy, order.miasto].filter(Boolean).join(', ')}
                  </div>
                )}
                {order.nip && (
                  <div style={{ color: 'var(--text-muted)' }}>🏢 NIP: {order.nip}</div>
                )}
                
                {/* Przycisk SMS */}
                {order.telefon && (
                  <div style={{ marginTop: '0.4rem', paddingTop: '0.3rem', borderTop: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleSendSms('order_ready')}
                        disabled={sendingSms}
                        style={{
                          padding: '4px 10px',
                          fontSize: '11px',
                          cursor: sendingSms ? 'not-allowed' : 'pointer',
                          backgroundColor: 'var(--success)',
                          color: 'white'
                        }}
                      >
                        📱 SMS: Gotowe
                      </button>
                      <button
                        onClick={() => handleSendSms('order_shipped')}
                        disabled={sendingSms}
                        style={{
                          padding: '4px 10px',
                          fontSize: '11px',
                          cursor: sendingSms ? 'not-allowed' : 'pointer',
                          backgroundColor: 'var(--info)',
                          color: 'white'
                        }}
                      >
                        📱 SMS: Wysłane
                      </button>
                    </div>
                    {smsMessage.text && (
                      <div style={{
                        marginTop: '4px',
                        padding: '3px 6px',
                        fontSize: '9px',
                        borderRadius: '3px',
                        backgroundColor: smsMessage.type === 'error' ? 'var(--danger-light)' : 'var(--success-light)',
                        color: smsMessage.type === 'error' ? 'var(--danger)' : 'var(--success)'
                      }}>
                        {smsMessage.text}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pozycje zamówienia */}
          <div style={{ marginTop: '0.5rem', backgroundColor: 'var(--card-bg)', borderRadius: '4px', border: '1px solid var(--border)' }}>
            <div style={{ 
              padding: '0.4rem 0.5rem', 
              backgroundColor: 'var(--border)', 
              borderBottom: '1px solid var(--border)',
              borderRadius: '4px 4px 0 0'
            }}>
              <h4 style={{ margin: 0, fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                📦 Pozycje zamówienia ({orderData.pozycje?.length || 0})
              </h4>
            </div>
            
            {!orderData.pozycje || orderData.pozycje.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '11px' }}>
                📭 Brak pozycji w zamówieniu
              </div>
            ) : (
              <div style={{ maxHeight: '250px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                  <thead style={{ backgroundColor: 'var(--bg)', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>#</th>
                      <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>Produkt</th>
                      <th style={{ padding: '5px 8px', textAlign: 'center', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>Ilość</th>
                      <th style={{ padding: '5px 8px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>Cena</th>
                      <th style={{ padding: '5px 8px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>Wartość</th>
                      <th style={{ padding: '5px 8px', textAlign: 'center', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>VAT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderData.pozycje.map((pozycja, index) => (
                      <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}>
                        <td style={{ padding: '4px 8px', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
                          {pozycja.pozycja || (index + 1)}
                        </td>
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid var(--border)' }}>
                          <div style={{ fontWeight: '500' }}>{pozycja.produkt_nazwa}</div>
                          {pozycja.ean && <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>EAN: {pozycja.ean}</div>}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                          <span style={{
                            padding: '2px 6px',
                            backgroundColor: 'var(--info-light)',
                            color: 'var(--info)',
                            borderRadius: '10px',
                            fontWeight: '600'
                          }}>
                            {pozycja.ilosc} {pozycja.jednostka || 'szt'}
                          </span>
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>
                          {formatPrice(pozycja.cena_jednostkowa)}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: '600', color: 'var(--success)', borderBottom: '1px solid var(--border)' }}>
                          {formatPrice(pozycja.wartosc_brutto)}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                          <span style={{
                            padding: '2px 4px',
                            backgroundColor: 'var(--warning-light)',
                            color: 'var(--warning)',
                            borderRadius: '3px',
                            fontSize: '9px',
                            fontWeight: '600'
                          }}>
                            {pozycja.stawka_vat || 23}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot style={{ backgroundColor: 'var(--border)' }}>
                    <tr>
                      <td colSpan="4" style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '600' }}>RAZEM:</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '700', color: 'var(--success)', fontSize: '11px' }}>
                        {formatPrice(orderData.wartosc_brutto)}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: '9px', color: 'var(--text-muted)' }}>
                        VAT: {formatPrice((orderData.wartosc_brutto || 0) - (orderData.wartosc_netto || 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
          
        {/* Footer modalu - styl POS */}
        <div style={{ 
          padding: '0.5rem 0.75rem', 
          backgroundColor: 'var(--bg)', 
          borderTop: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '6px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: '500',
              border: '1px solid var(--border)',
              cursor: 'pointer',
              backgroundColor: 'var(--card-bg)',
              color: 'var(--text-secondary)'
            }}
          >
            ✕ Zamknij
          </button>
          
          <button
            onClick={handleGenerateReceipt}
            disabled={generatingReceipt}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: generatingReceipt ? 'not-allowed' : 'pointer',
              border: '1px solid var(--info)', color: 'var(--info)' ,
              opacity: generatingReceipt ? 0.7 : 1
            }}
          >
            {generatingReceipt ? '⏳ Generowanie...' : '🧾 Paragon'}
          </button>
          
          {order.status !== 'completed' && (
            <button
              onClick={onEdit}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                backgroundColor: 'var(--warning)',
                color: 'var(--text)'
              }}
            >
              ✏️ Edytuj
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
